import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// The spreadsheet's Type column, exactly — see supabase/schema_issue_types.sql.
export const ISSUE_TYPES = ["new_cr", "data_load", "setup", "integration_issue"];
export const ISSUE_TYPE_LABELS = {
  new_cr: "New CR",
  data_load: "Data Load",
  setup: "Setup",
  integration_issue: "Integration Issue",
};

// issues.status reuses the exact same 6 values StatusBadge/StatusPill already
// style for tasks (open/wip/uat/hold/closed) — see schema_issues.sql. "live"
// was dropped from the workflow — it's still a valid DB value on old rows,
// just no longer offered as a choice.
export const ISSUE_STATUSES = ["open", "wip", "uat", "hold", "closed"];

// Generous cap on free-text fields — high enough to never get in the way of
// a real description, just a backstop against pasting something absurd.
export const ISSUE_TEXT_MAX_LENGTH = 4000;

// No default type — the reporter must pick one, not inherit whatever the
// last person who opened this modal happened to leave selected.
const emptyForm = () => ({ description: "", type: "", projectId: "", taskId: "" });

/*
  Reports a new issue against one project or task. Controlled by the parent
  (open/onOpenChange), the same convention InviteEmployeesModal uses — the
  caller owns the trigger (the warning icon on that entity's card/row/
  header, or the "New Issue" button on the Issues page), this component only
  owns the form.

  Two ways to use it:
  - entityType + entityId + entityLabel fixed by the caller (the warning-icon
    flow from a project/task's own page) — the entity is shown read-only.
  - entityType left undefined, with projectOptions/taskOptions passed instead
    (the Issues page's own "New Issue" button) — the reporter always picks a
    project first, then optionally narrows to one of that project's tasks
    (taskOptions filtered by task.project_id). Leaving the task unset reports
    the issue against the project itself. Initiatives are deliberately not
    offered here or as a fixed entityType any more; issues no longer
    associate with initiatives.
*/
export default function ReportIssueModal({ open, onOpenChange, entityType, entityId, entityLabel, projectOptions, taskOptions, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const needsPicker = !entityType;
  const tasksForProject = (taskOptions || []).filter((t) => t.project_id === form.projectId);

  const close = (v) => {
    onOpenChange(v);
    if (!v) setForm(emptyForm());
  };

  const submit = async () => {
    const resolvedType = entityType || (form.taskId ? "task" : "project");
    const resolvedId = entityType ? entityId : (form.taskId || form.projectId);
    if (needsPicker && !form.projectId) { toast.error("Select a project"); return; }
    if (!form.type) { toast.error("Select a type"); return; }
    if (!form.description.trim()) { toast.error("Describe the issue"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/issues", {
        [`${resolvedType}_id`]: resolvedId,
        description: form.description.trim(),
        type: form.type,
      });
      toast.success(`Issue ${data.issue_number} reported`);
      close(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-sm max-w-lg" onClick={(e) => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="font-display">Report an Issue</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {needsPicker ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="trk-uppercase-label mb-2 block">Project <span className="text-red-500">*</span></Label>
                <Select value={form.projectId} onValueChange={(v) => setForm({ ...form, projectId: v, taskId: "" })}>
                  <SelectTrigger className="rounded-sm" data-testid="issue-project-select"><SelectValue placeholder="Select a project…" /></SelectTrigger>
                  <SelectContent className="max-w-[20rem]">
                    {(projectOptions || []).map((p) => (
                      <SelectItem key={p._id} value={p._id} className="whitespace-normal">{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="trk-uppercase-label mb-2 block">Task</Label>
                <Select value={form.taskId} onValueChange={(v) => setForm({ ...form, taskId: v })} disabled={!form.projectId}>
                  <SelectTrigger className="rounded-sm" data-testid="issue-task-select"><SelectValue placeholder={form.projectId ? "Whole project…" : "Select a project first"} /></SelectTrigger>
                  <SelectContent className="max-w-[20rem]">
                    {tasksForProject.map((t) => (
                      <SelectItem key={t._id} value={t._id} className="whitespace-normal">{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div>
              <Label className="trk-uppercase-label mb-2 block capitalize">{entityType}</Label>
              <div className="text-sm font-medium text-[#0F172A] break-words">{entityLabel}</div>
            </div>
          )}
          <div>
            <Label className="trk-uppercase-label mb-2 block">Type <span className="text-red-500">*</span></Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="issue-type-select"><SelectValue placeholder="Select a type…" /></SelectTrigger>
              <SelectContent>
                {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{ISSUE_TYPE_LABELS[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="trk-uppercase-label mb-2 block">Issue Description <span className="text-red-500">*</span></Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded-sm"
              rows={4}
              maxLength={ISSUE_TEXT_MAX_LENGTH}
              placeholder="What's wrong, and what did you expect instead?"
              data-testid="issue-description-input"
              autoFocus
            />
          </div>
          <div className="text-xs text-zinc-500">
            Raised by {user?.name || user?.email} on {new Date().toLocaleDateString()}. An issue number is assigned automatically on submit.
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="save-issue-button"
            className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]"
          >
            {saving ? "Reporting…" : "Report Issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
